import { action, runInAction, observable } from 'mobx';
import reconciliationController from './controller';
import {
  ReconciliationActInterface,
  CreateReconciliationActRequest,
  AddReconciliationActRequest,
  deleteReconciliationActRequest,
  ReconciliationActContentInterface,
  ReconciliationActCommentInterface,
  ReconciliationActRejectInterface,
} from './reconciliationApi';

export interface ReconciliationStore {
  reconciliationAct: ReconciliationActInterface,
  reconciliationActById: string, // по какому reconciliationActId находятся данные в reconciliationAct
  reconciliationActContent: ReconciliationActContentInterface[],
  reconciliationActContentById: string, // по какому reconciliationActId находятся данные в reconciliationActContent
  reconciliationActList: ReconciliationActInterface[], // список для отчёта по актам взаиморасчёта
  reconciliationActListForSelect: ReconciliationActInterface[], // список для отчёта  выбора из списка взаиморасчётов
  lastCreatedReconciliationAct: ReconciliationActInterface, // последний созданный акт
  lastUpdatedReconciliationAct: ReconciliationActInterface, // акт, в который добавлялись счета
  reconciliationActCommentHistory: ReconciliationActCommentInterface[], // история комментариев по акты
  reconciliationActCommentHistoryById: string, // по какому reconciliationActId находятся данные в reconciliationActCommentHistory
  loading: boolean,
  error: any,
}

const initialValues: ReconciliationStore = {
  reconciliationActList: [],
  reconciliationActListForSelect: [],
  reconciliationAct: null,
  reconciliationActContent: [],
  reconciliationActContentById: null,
  reconciliationActById: null,
  lastCreatedReconciliationAct: null,
  lastUpdatedReconciliationAct: null,
  reconciliationActCommentHistory: [],
  reconciliationActCommentHistoryById: null,
  loading: false,
  error: null,
};
export const reconciliationStore = observable(initialValues);

export const reconciliationActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      reconciliationStore.error = errorData;
      reconciliationStore.loading = false;
      throw errorData;
    }
  }),

  // по 6243, кажется, проще не кидать ошибку выше по коду
  // обработку ошибок для задачи 6243 обрабатываю через reconciliationStore.error в коде
  handleErrorWithoutThrow: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      reconciliationStore.loading = false;
      reconciliationStore.error = errorData;
    }
  }),

  // Возвращает Акт взаиморасчётов по его id
  async getReconciliationAct(id) {
    if (id) {
      reconciliationActions.clearError();
      reconciliationActions.setLoading();
      runInAction(() => {
        reconciliationStore.reconciliationActById = id;
      });
      const response = await reconciliationController.getReconciliationAct(id);
      reconciliationActions.handleError(response);
      runInAction(() => {
        reconciliationStore.reconciliationAct = response.data;
        reconciliationStore.loading = false;
      });
    }
  },

  // Возвращает список счетов, входящих в Акт взаиморасчётов по его id
  async getReconciliationActContent(id) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    runInAction(() => {
      reconciliationStore.reconciliationActContentById = id;
    });
    const response = await reconciliationController.getReconciliationActContent(id);
    reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationActContent = response.data;
      reconciliationStore.loading = false;
    });
  },

  // Найти Акты взаиморасчётов согласно переданному в запросе фильтру
  async getReconciliationActList(data) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    const response = await reconciliationController.getReconciliationActList(data);
    reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationActList = response.data.items
        .sort((a, b) => {
          return (a.createdDate > b.createdDate) ? -1 : 1;
        });
      reconciliationStore.loading = false;
    });
  },

  // Найти Акты взаиморасчётов согласно переданному в запросе фильтру
  async getReconciliationActListForSelect(data) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    const response = await reconciliationController.getReconciliationActList(data);
    reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationActListForSelect = response.data.items.sort((a, b) => {
        return (a.createdDate > b.createdDate) ? -1 : 1;
      });
      reconciliationStore.loading = false;
    });
  },

  async createReconciliationActAndAddInvoices(data: CreateReconciliationActRequest) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.createReconciliationActAndAddInvoices(data);
    await reconciliationActions.handleErrorWithoutThrow(response);
    runInAction(() => {
      reconciliationStore.lastCreatedReconciliationAct = response.data;
      reconciliationStore.loading = false;
    });
  },

  async addInvoicesToReconciliationAct(data: AddReconciliationActRequest) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.addInvoicesToReconciliationAct(data);
    await reconciliationActions.handleErrorWithoutThrow(response);
    runInAction(() => {
      reconciliationStore.lastUpdatedReconciliationAct = response.data;
      reconciliationStore.loading = false;
    });
  },

  async deleteInvoiceFromReconciliationAct(data: deleteReconciliationActRequest) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    const response = await reconciliationController.deleteInvoiceFromReconciliationAct(data);
    reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.loading = false;
    });
  },

  async clearReconciliationAct() {
    try {
      runInAction(() => {
        reconciliationStore.reconciliationAct = null;
        reconciliationStore.reconciliationActById = null;
        reconciliationStore.reconciliationActCommentHistory = [];
        reconciliationStore.reconciliationActCommentHistoryById = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async clearReconciliationList() {
    try {
      runInAction(() => {
        reconciliationStore.reconciliationActList = [];
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async clearReconciliationListForSelect() {
    try {
      runInAction(() => {
        reconciliationStore.reconciliationActListForSelect = [];
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async clearReconciliationActContent() {
    try {
      runInAction(() => {
        reconciliationStore.reconciliationActContent = [];
        reconciliationStore.reconciliationActContentById = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async clearLastCreatedReconciliationAct() {
    try {
      runInAction(() => {
        reconciliationStore.lastCreatedReconciliationAct = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  async clearLastUpdatedReconciliationAct() {
    try {
      runInAction(() => {
        reconciliationStore.lastUpdatedReconciliationAct = null;
      });
    } catch (err) {
      this.handleError(err);
    }
  },

  clearError: action(() => {
    reconciliationStore.error = null;
  }),

  clearLoading: action(() => {
    reconciliationStore.loading = false;
  }),

  setLoading: action(() => {
    reconciliationStore.loading = true;
  }),

  // Пересчитать комиссии по счёту - в параметрах передаём id счёта
  async recalculateInvoice(id: string) {
    const response = await reconciliationController.recalculateInvoice(id);
    await reconciliationActions.handleErrorWithoutThrow(response);
  },

  // Уведомить о пересчёте комиссий по акту -  в параметрах передаём id акта
  async recalculateRecalculationNotify(id: string) {
    const response = await reconciliationController.recalculateRecalculationNotify(id);
    await reconciliationActions.handleErrorWithoutThrow(response);
  },

  // получить историю комментариев
  async getReconciliationActCommentHistory(id) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    runInAction(() => {
      reconciliationStore.reconciliationActCommentHistoryById = id;
    });
    const response = await reconciliationController.getReconciliationActCommentHistory(id);
    reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationActCommentHistory = response.data;
    });
    reconciliationStore.loading = false;
  },

  // Принять указанный Акт, т.е. перевести его из состояния 'Акт сформирован' в состояние 'Акт согласован'
  async reconciliationRatify(id: string) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.reconciliationRatify(id);
    await reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationAct = response.data;
      reconciliationActions.clearLoading();
    });
  },

  // Отклонить указанный Акт, т.е. перевести его из состояния 'Акт сформирован' или 'Акт согласован' в состояние 'Акт отклонён'
  async reconciliationReject(data: ReconciliationActRejectInterface) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.reconciliationReject(data);
    await reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationAct = response.data;
      reconciliationActions.clearLoading();
    });
  },

  // Отправить на проверку указанный Акт, т.е. перевести его из состояния 'Акт отклонён' в состояние 'Акт сформирован'
  async reconciliationVerify(id: string) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.reconciliationVerify(id);
    await reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationAct = response.data;
      reconciliationActions.clearLoading();
    });
  },

  // Акт оплачен
  async reconciliationFinalize(id: string) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.reconciliationFinalize(id);
    await reconciliationActions.handleError(response);
    runInAction(() => {
      reconciliationStore.reconciliationAct = response.data;
      reconciliationActions.clearLoading();
    });
  },

  // Полностью удалить указанный Акт и историю комментариев к нему, также отвязав от него счета
  async reconciliationHardDelete(id: string) {
    reconciliationActions.clearError();
    reconciliationActions.setLoading();
    reconciliationActions.clearLastCreatedReconciliationAct();
    reconciliationActions.clearLastUpdatedReconciliationAct();
    const response = await reconciliationController.reconciliationHardDelete(id);
    await reconciliationActions.handleError(response);
    if (!reconciliationStore.error) {
      runInAction(() => {
        // после удаления зачистим список актов - уберём удалённый
        if (reconciliationStore.reconciliationActList) {
          reconciliationStore.reconciliationActList = reconciliationStore.reconciliationActList
            .filter((v) => v.id !== id);
        }
        reconciliationActions.clearReconciliationAct();
        reconciliationActions.clearReconciliationActContent();
        reconciliationActions.clearLoading();
      });
    }
  },

};
